import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/feature/Seo";

const contactItems = [
  { icon: "ri-mail-line", label: "邮箱", value: "feedback@91video-demo.example" },
  { icon: "ri-weibo-line", label: "微博", value: "@91视频网站演示账号" },
  { icon: "ri-wechat-line", label: "公众号", value: "91视频网站服务号（示例）" },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 10:00 - 18:00" },
];

const values = [
  { icon: "ri-stack-line", title: "内容清晰分类", desc: "电影、电视剧、综艺三大频道，配合类型、地区、年份多维筛选。" },
  { icon: "ri-search-eye-line", title: "查找更快", desc: "搜索支持片名、类型与地区关键词匹配，结果直达详情页。" },
  { icon: "ri-device-line", title: "全端适配", desc: "桌面浏览与手机访问均可获得顺畅的信息浏览体验。" },
];

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const [sent, setSent] = useState(false);

  const update = (key: "name" | "email" | "message", value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = "请填写姓名或昵称";
    if (!form.email.trim()) {
      nextErrors.email = "请填写邮箱";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "邮箱格式看起来不太对";
    }
    if (!form.message.trim()) nextErrors.message = "请写下你的反馈内容";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSent(false);
      setNotice("");
      return;
    }
    setSent(true);
    setNotice("感谢反馈，稍后处理（当前为演示，内容不会被真实提交）");
  };

  return (
    <SiteLayout active="about">
      <Seo
        title="关于我们 - 91视频网站"
        description="了解 91视频网站的定位与服务范畴：影视资源展示与浏览平台，提供电影、电视剧、综艺分类筛选、搜索与详情浏览。欢迎通过反馈表单或邮箱联系我们。"
        keywords="关于91视频网站,91视频网站介绍,影视资源平台,联系我们"
      />

      <div className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 xl:px-10 py-10 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-secondary-300 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-secondary-400">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
            关于 91视频网站
          </h1>
          <p className="mt-3 text-sm md:text-[15px] leading-7 text-background-200 max-w-3xl">
            我们希望把最新电影、热播剧集与优质综艺，整理成一份看得清楚、找得到的片单。
            91视频网站专注影视内容的展示与浏览，帮助你用更少的步骤，找到下一部想看的作品。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="px-4 py-3 rounded-lg bg-primary-800/60 border border-primary-700">
              <p className="font-heading text-xl font-bold text-secondary-400">40+</p>
              <p className="text-[11px] text-background-300 mt-0.5">示例影视作品</p>
            </div>
            <div className="px-4 py-3 rounded-lg bg-primary-800/60 border border-primary-700">
              <p className="font-heading text-xl font-bold text-secondary-400">3</p>
              <p className="text-[11px] text-background-300 mt-0.5">主内容频道</p>
            </div>
            <div className="px-4 py-3 rounded-lg bg-primary-800/60 border border-primary-700">
              <p className="font-heading text-xl font-bold text-secondary-400">每日</p>
              <p className="text-[11px] text-background-300 mt-0.5">更新展示</p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 xl:px-10 py-9 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <h2 className="font-heading text-base font-semibold text-foreground-950 mt-3">{item.title}</h2>
              <p className="text-sm leading-6 text-foreground-600 mt-1.5">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 mt-10 md:mt-12">
          <section aria-label="网站简介" className="lg:col-span-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              我们在做什么
            </h2>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <p className="text-sm leading-7 text-foreground-700">
                91视频网站是一个面向普通影视爱好者的内容门户。我们把电影、电视剧与综艺按频道归类，
                并为每部作品整理封面、标题、类型、地区、年份、时长与剧情简介等基础信息，
                让你在决定要不要看之前，先花三十秒了解它到底讲了什么。
              </p>
              <p className="text-sm leading-7 text-foreground-700 mt-3">
                分类页支持类型、地区、年份的组合筛选，并按更新时间、点击量、评分或名称排序；
                首页会展示最近更新与播放量靠前的作品；详情页则会根据类型与地区的相似度为作品补充推荐，
                方便你顺着一个故事找到下一个。
              </p>
              <div className="mt-4 rounded-md bg-secondary-50 border border-secondary-200 p-3.5">
                <p className="text-xs leading-6 text-secondary-900">
                  <i className="ri-information-line mr-1"></i>
                  重要说明：站内所有影视作品名称、导演、演员、评分与剧情均为虚构的示例内容，
                  本站为页面结构与交互体验的演示站点，不提供真实影片播放与下载服务。
                </p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/category/movie"
                  className="h-9 px-4 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  开始浏览电影
                </Link>
                <Link
                  to="/search"
                  className="h-9 px-4 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-search-line mr-1.5"></i>
                  搜索影视
                </Link>
              </div>
            </div>

            <section aria-label="联系方式" className="mt-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                联系方式
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {contactItems.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="w-9 h-9 rounded-md bg-background-100 text-primary-600 flex items-center justify-center shrink-0">
                      <i className={item.icon}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="text-sm text-foreground-900 mt-0.5 break-all">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-400 mt-3">以上联系方式均为示例信息，仅用于页面展示。</p>
            </section>
          </section>

          <section aria-label="用户反馈" className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-secondary-500"></span>
              用户反馈
            </h2>
            <form
              onSubmit={submit}
              className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5"
              noValidate
            >
              <p className="text-xs leading-6 text-foreground-500 mb-4">
                有想看的片子找不到？页面哪里用起来别扭？写下你的想法，我们会认真看。
              </p>
              <div className="space-y-3.5">
                <div>
                  <label htmlFor="fb-name" className="block text-xs text-foreground-600 mb-1.5">
                    姓名 / 昵称
                  </label>
                  <input
                    id="fb-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="怎么称呼你"
                    className={`w-full h-10 px-3 rounded-md bg-background-50 border text-sm outline-none transition-colors placeholder:text-foreground-400 ${
                      errors.name ? "border-accent-500" : "border-background-300 focus:border-primary-400"
                    }`}
                  />
                  {errors.name && <p className="text-[11px] text-accent-600 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="fb-email" className="block text-xs text-foreground-600 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="name@example.com"
                    className={`w-full h-10 px-3 rounded-md bg-background-50 border text-sm outline-none transition-colors placeholder:text-foreground-400 ${
                      errors.email ? "border-accent-500" : "border-background-300 focus:border-primary-400"
                    }`}
                  />
                  {errors.email && <p className="text-[11px] text-accent-600 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="fb-message" className="block text-xs text-foreground-600 mb-1.5">
                    反馈内容
                  </label>
                  <textarea
                    id="fb-message"
                    value={form.message}
                    maxLength={500}
                    onChange={(e) => update("message", e.target.value.slice(0, 500))}
                    rows={4}
                    placeholder="例如：希望增加更多悬疑类型的片单"
                    className={`w-full px-3 py-2.5 rounded-md bg-background-50 border text-sm outline-none transition-colors resize-none placeholder:text-foreground-400 ${
                      errors.message ? "border-accent-500" : "border-background-300 focus:border-primary-400"
                    }`}
                  ></textarea>
                  <div className="flex items-center justify-between mt-1">
                    {errors.message ? (
                      <p className="text-[11px] text-accent-600">{errors.message}</p>
                    ) : (
                      <span className="text-[11px] text-foreground-400">最多 500 字</span>
                    )}
                    <span className="text-[11px] text-foreground-400">{form.message.length}/500</span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="mt-4 w-full h-10 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                提交反馈
              </button>

              {notice && (
                <div className="mt-3 rounded-md bg-secondary-50 border border-secondary-200 px-3 py-2.5 flex items-start gap-2 animate-fade-in">
                  <i className="ri-checkbox-circle-line text-secondary-700 mt-0.5"></i>
                  <p className="text-xs leading-5 text-secondary-900">{notice}</p>
                </div>
              )}
              {!sent && (
                <p className="text-[11px] text-foreground-400 mt-3">
                  本表单为演示示例，不收集、不保存任何提交内容。
                </p>
              )}
            </form>
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}